/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {
    AbstractControl,
    ValidatorFn,
    Validators
} from '@angular/forms';
import {
    Observable,
    of,
    Subscription,
    timer
} from 'rxjs';
import {SpecialOrderMaterialDetailModel} from '../../../../api/ms-specialorders/contracts/special-order-material-detail.model';
import {MaterialDetailModel} from '../../../../api/ms-specialorders/contracts/material-detail.model';
import {MaterialService} from '../../../../api/ms-specialorders/material/material.service';
import {TooltipEnum} from '../../../../shared/tooltip/tooltip.enum';
import {WarningsEnum} from '../../../../shared/warnings/warnings.enum';
import {TypeaheadHelperService} from '../../../../shared/services/typeahead-helper.service';
import {InputAutocompleteComponent} from '../../../../shared/input/input-autocomplete/input-autocomplete.component';
import {InputTextComponent} from '../../../../shared/input/input-text/input-text.component';
import {MandatoryMessageTypeEnum} from '../../../../shared/input/service/mandatory-message-type.enum';
import {InputMessagesService} from '../../../../shared/input/service/input-messages.service';
import {objectsValidator} from '../../../../shared/input/input-validators';
import {
    finalize,
    map
} from 'rxjs/operators';
import {StatefulComponent} from '../../../../shared/stateful/stateful.component';
import {MaterialListModalStateModel} from './material-list-modal-state.model';
import {DropdownButtonModel} from './models/dropdown-button.model';
import {SpinnerService} from '../../../../shared/spinner/spinner.service';
import {SpinnerContextTypeEnum} from '../../../../shared/spinner/spinner-context-type.enum';

@Component({
    selector: 'app-material-list-modal',
    templateUrl: './material-list-modal.component.html',
    styleUrls: ['./material-list-modal.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MaterialListModalComponent extends StatefulComponent<MaterialListModalStateModel> implements OnInit, OnDestroy {

    /** Inputs and Outputs **/
    @Output() addClick: EventEmitter<SpecialOrderMaterialDetailModel> = new EventEmitter();
    @Input() supplyingPackingPlantCode: string;
    @Input() maxNumberOfMaterials: number;
    @Input() materialsLength: number;
    /** Labels variables **/
    @ViewChild('material', {static: true}) material: InputAutocompleteComponent;
    @ViewChild('modelType', {static: true}) modelType: InputTextComponent;
    @ViewChild('lotNumber', {static: true}) lotNumber: InputTextComponent;
    @ViewChild('mcdrNumber', {static: true}) mcdrNumber: InputTextComponent;
    @ViewChild('igefNumber', {static: true}) igefNumber: InputTextComponent;
    @ViewChild('colourCode', {static: true}) colourCode: InputTextComponent;
    @ViewChild('orderedQuantity', {static: true}) orderedQuantity: InputTextComponent;
    @ViewChild('dropdownButtonTemplate') dropdownButtonTemplate: TemplateRef<unknown>;

    validators: { [key: string]: ValidatorFn[] } = {
        material: [objectsValidator],
        modelType: [Validators.maxLength(4), Validators.pattern(/^(?=.*[0-9])(?=.*[a-zA-Z])([a-zA-Z0-9]){4}$/)],
        lotNumber: [Validators.maxLength(4), Validators.pattern(/^[0-9]{0,4}$/)],
        mcdrNumber: [Validators.maxLength(9), Validators.pattern(/^[0-9]{0,9}$/)],
        igefNumber: [Validators.maxLength(8), Validators.pattern(/^[0-9]{4}[a-zA-Z][0-9]{3}|[0-9]{8}$/)],
        colourCode: [Validators.maxLength(4), Validators.pattern(/^[0-9a-zA-Z]{4}$/)],
        orderedQuantity: [Validators.maxLength(6), Validators.pattern(/^[1-9]{1}[0-9]{0,5}$/)]
    };

    /** Warnings and tooltips visibility **/
    /** Selected material details **/
    public selectedMaterial: SpecialOrderMaterialDetailModel;
    public units: string;
    public minimumQuantity: number;
    public reduceMaterialPadding = false;
    public reduceOrderedQuantityPadding = false;
    public showBoxOrderQuantityInfo = false;
    public warningBoxLevel = WarningsEnum.INFO;
    readonly MESSAGE_INACTIVE_MATERIAL = 'This material is run out';
    TooltipEnum = TooltipEnum;
    WarningsEnum = WarningsEnum;
    materialMessages = new Map<WarningsEnum, string[]>();
    modelTypeMessages = new Map<WarningsEnum, string[]>();
    lotNumberMessages = new Map<WarningsEnum, string[]>();
    mcdrNumberMessages = new Map<WarningsEnum, string[]>();
    igefNumberMessages = new Map<WarningsEnum, string[]>();
    colourCodeMessages = new Map<WarningsEnum, string[]>();
    orderedQuantityMessages = new Map<WarningsEnum, string[]>();
    /** Subscriptions variable **/
    private subscriptions = new Subscription();
    private currentMaterial: SpecialOrderMaterialDetailModel = Object.create(null);
    /** Interactive Material Search **/
    private readonly RESULTS_INCREMENT = 20;
    /** Loading Spinner initialization **/
    private readonly LOCAL_LOADING_INSTANCE_KEY = 'MATERIAL_LIST_MODAL_LOADING';
    public spinnerObservable = this.spinnerService.onLocalChange(this.LOCAL_LOADING_INSTANCE_KEY);
    /** Tooltip message when View More button is disabled **/
    readonly MESSAGE_VIEW_MORE = 'There are no more materials to be found';

    constructor(
            public activeModal: NgbActiveModal,
            public typeahead: TypeaheadHelperService,
            private materialService: MaterialService,
            private inputMessagesService: InputMessagesService,
            private spinnerService: SpinnerService) {
        super();

        this.subscriptions.add(this.spinnerObservable.subscribe((isLoading) => {
            this.storeState({isLoading: isLoading});
        }));
    }

    ngOnInit(): void {
        timer(0)
                .subscribe(() => {
                    this.setSelectedMaterialFormValues();
                    this.toggleEnabledMaterialNumberInput();
                    this.setUnits();
                    this.setMinimumOrderQuantity();
                    this.managePadding();
                });

        this.storeState({
            firstResultIndex: 0,
            previousSearchTerm: '',
            savedMaterials: [],
            returnedMaterials: []
        });
    }

    ngOnDestroy(): void {
        if (this.subscriptions) {
            this.subscriptions.unsubscribe();
        }
    }

    getMaterialFieldsValues = (material: SpecialOrderMaterialDetailModel): SpecialOrderMaterialDetailModel => {
        material.lotNumber = material.lotNumber && material.lotNumber.toString().length > 0 ? material.lotNumber : null;
        material.mcdrNumber = material.mcdrNumber && material.mcdrNumber.toString().length > 0 ? material.mcdrNumber : null;
        material.modelType = material.modelType && material.modelType.length > 0 ? material.modelType : null;
        material.orderedQuantity = material.orderedQuantity && material.orderedQuantity.toString().length > 0 ?
                material.orderedQuantity : null;
        material.igefNumber = material.igefNumber && material.igefNumber.toString().length > 0 ? material.igefNumber : null;
        material.colourCode = material.colourCode && material.colourCode.toString().length > 0 ? material.colourCode : null;
        return material;
    };

    add = (): void => {
        const materialClone = Object.assign({}, this.currentMaterial);
        this.emitMaterial(this.getMaterialFieldsValues(materialClone));
        this.activeModal.close();
    };

    addAndNew = (): void => {
        const materialClone = Object.assign({}, this.currentMaterial);
        this.emitMaterial(this.getMaterialFieldsValues(materialClone));
        this.resetForm();
    };

    disableAddButtons = (): boolean => this.currentMaterial.material == null || !this.formIsValid();

    disabledAdd = (): boolean => this.disableAddButtons() || this.materialsLength > this.maxNumberOfMaterials;

    disabledAddAndNew = (): boolean => this.disableAddButtons() || this.materialsLength >= this.maxNumberOfMaterials - 1;

    disabledViewMore = (): boolean => !this.hasMoreMaterialsToReturn() || this.state.isLoading;

    hasMoreMaterialsToReturn = (): boolean => this.RESULTS_INCREMENT <= this.state.returnedMaterials.length;

    formIsValid = (): boolean => this.material.isValid
            && this.modelType.isValid
            && this.lotNumber.isValid
            && this.mcdrNumber.isValid
            && this.orderedQuantity.isValid
            && this.igefNumber.isValid
            && this.colourCode.isValid;

    emitMaterial = (material: SpecialOrderMaterialDetailModel): void => {
        if (this.selectedMaterial) {
            material.material = this.selectedMaterial.material;
            material.position = this.selectedMaterial.position;
        }
        material.orderedQuantity = Number(material.orderedQuantity);
        this.addClick.emit(this.getMaterialFieldsValues(material));
    };

    searchMaterials = (term: string): Observable<MaterialDetailModel[]> => {
        let materialsResult = of([]);
        if (term.length > 0) {
            this.evaluateCurrentResultsReset(term);
            materialsResult = this.materialService.getAutocompleteMaterialsList(term, this.supplyingPackingPlantCode,
                    this.state.firstResultIndex, this.RESULTS_INCREMENT)
                    .pipe(map((results) => {
                        this.storeState({returnedMaterials: [...results]});
                        const dropdownButtonModel: DropdownButtonModel = {
                            template: this.dropdownButtonTemplate,
                            action: this.dropdownButtonClick
                        };
                        const finalResults = [...this.state.savedMaterials, ...results, dropdownButtonModel];
                        return finalResults;
                    }), finalize(() => {
                        this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
                        this.material.enable();
                    }));
        }
        return materialsResult;
    };

    dropdownButtonClick = (event: MouseEvent): void => {
        this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
        this.material.disable();

        if (event != null) {
            event.stopPropagation(); // stop default behaviour
        }

        this.storeState({
            firstResultIndex: this.state.firstResultIndex + this.RESULTS_INCREMENT
        });
        this.state.savedMaterials.push(...this.state.returnedMaterials);

        this.material?.reloadDropdownList(); // update the dropdown list
    };

    materialChange = (material: MaterialDetailModel): void => {
        this.currentMaterial.material = typeof material === 'object' && material != null ? material : null;
        this.clearMaterialMessages();

        if (typeof material === 'object' && material != null) {
            this.showBoxOrderQuantityInfo = material.minQuantity != null && material.minQuantity > 0;
            this.units = material.salesUnit;
            this.minimumQuantity = material.minQuantity;
        } else {
            this.showBoxOrderQuantityInfo = false;
            this.units = '';
            this.minimumQuantity = null;
        }

        this.manageMaterialVersionWarning(material);
        this.manageMaterialShelfLifeWarning(material);
        this.manageMaterialInactiveError(material);

        this.orderedQuantity.refresh();
    };

    materialStateChange = (control: AbstractControl): void => {
        this.inputMessagesService.mandatoryWarnings(control,
                this.materialMessages,
                MandatoryMessageTypeEnum.SAVE_AND_SEND);
        this.inputMessagesService.invalidInputValueError(control,
                this.materialMessages,
                this.material.label);
        this.materialMessages = new Map(this.materialMessages);
        this.managePadding();
    };

    modelTypeChange = (value: string): void => {
        this.currentMaterial.modelType = value?.toUpperCase();
    };

    modelTypeStateChange = (control: AbstractControl): void => {
        this.inputMessagesService.invalidInputValueError(control,
                this.modelTypeMessages,
                this.modelType.label,
                'KE98');
        this.modelTypeMessages = new Map(this.modelTypeMessages);
    };

    lotNumberChange = (value: string): void => {
        this.currentMaterial.lotNumber = value;
    };

    lotNumberStateChange = (control: AbstractControl): void => {
        this.inputMessagesService.invalidInputValueError(control,
                this.lotNumberMessages,
                this.lotNumber.label,
                '0000');
        this.lotNumberMessages = new Map(this.lotNumberMessages);
    };

    mcdrNumberChange = (value: string): void => {
        this.currentMaterial.mcdrNumber = value;
    };

    mcdrNumberStateChange = (control: AbstractControl): void => {
        this.inputMessagesService.invalidInputValueError(control,
                this.mcdrNumberMessages,
                this.mcdrNumber.label,
                '000000000');
        this.mcdrNumberMessages = new Map(this.mcdrNumberMessages);
    };

    igefNumberChange = (value: string): void => {
        this.currentMaterial.igefNumber = value?.toUpperCase();
    };

    igefNumberStateChange = (control: AbstractControl): void => {
        this.inputMessagesService.invalidInputValueError(control,
                this.igefNumberMessages,
                this.igefNumber.label,
                '9504N408');
        this.igefNumberMessages = new Map(this.igefNumberMessages);
    };

    colourCodeChange = (value: string): void => {
        this.currentMaterial.colourCode = value?.toUpperCase();
    };

    colourCodeStateChange = (control: AbstractControl): void => {
        this.inputMessagesService.invalidInputValueError(control,
                this.colourCodeMessages,
                this.colourCode.label,
                'WC4L');
        this.colourCodeMessages = new Map(this.colourCodeMessages);
    };

    orderQuantityChange = (value: string): void => {
        let quantity: number;
        if (/^\d+$/.test(value)) {
            quantity = parseInt(value, 10);
        } else {
            // FIXME: this variable is a number but a string is being attributed to it
            // @ts-ignore
            quantity = value;
        }
        this.currentMaterial.orderedQuantity = quantity;
        this.clearOrderedQuantityMessages();

        this.manageMaterialOrderedQuantityWarning(this.minimumQuantity, quantity);
        this.managePadding();
    };

    orderQuantityStateChange = (control: AbstractControl): void => {
        this.inputMessagesService.mandatoryWarnings(control,
                this.orderedQuantityMessages,
                MandatoryMessageTypeEnum.SEND);
        this.inputMessagesService.invalidInputValueError(control,
                this.orderedQuantityMessages,
                this.orderedQuantity.label);
        this.orderedQuantityMessages = new Map(this.orderedQuantityMessages);
    };

    setUnits = (): void => {
        if (this.selectedMaterial && this.selectedMaterial.material) {
            this.units = this.selectedMaterial.material.salesUnit;
        } else {
            this.units = '';
        }
    };

    setMinimumOrderQuantity = (): void => {
        if (this.selectedMaterial && this.selectedMaterial.material) {
            this.minimumQuantity = this.selectedMaterial.material.minQuantity;
        }
    };

    /** Sets ordered quantity warning visibility**/
    manageMaterialOrderedQuantityWarning(minQuantity: number, orderedQuantity: number): void {
        if ((minQuantity != null && minQuantity > 0)
                && (orderedQuantity != null && orderedQuantity > 0)
                && (orderedQuantity % minQuantity !== 0)) {
            this.disableAddButtons();
            this.orderedQuantity.markAsInvalid();
            this.setOrderedQuantityMessage(WarningsEnum.ERROR, `Order quantity must be ${minQuantity} or multiple`);
        }
    }

    getOrderQuantityPlaceholder(): string {
        if (this.minimumQuantity != null && this.minimumQuantity > 0) {
            return 'e.g. ' + this.minimumQuantity;
        } else {
            return 'e.g. 1';
        }
    }

    manageMaterialInactiveError(material: MaterialDetailModel): void {
        if (typeof material === 'object' && material != null && material.active === false) {
            this.material.markAsInvalid();
            this.clearMaterialMessages();
            this.addMaterialMessage(WarningsEnum.ERROR, this.MESSAGE_INACTIVE_MATERIAL);
        }
    }

    /** Sets material shelf-life visibility and label **/
    manageMaterialShelfLifeWarning(material: MaterialDetailModel): void {
        if (typeof material === 'object' && material != null && material.shelfLife > 0 && material.shelfLifePeriodType != null) {
            this.addMaterialMessage(WarningsEnum.WARNING, `Shelf life might be less than ${material.shelfLife} ` +
                    `${material.shelfLifePeriodType.name}. Please consider appropriate transport mode`);
        }
    }

    /** Sets material version warning visibility and label **/
    manageMaterialVersionWarning(material: MaterialDetailModel): void {
        if (typeof material === 'object' && material !== null) {
            if (material.latestVersion != null) {
                if (material.version !== material.latestVersion) {
                    const materialNumber = material.number.split('-')[0];
                    this.addMaterialMessage(WarningsEnum.WARNING, `Last shipped material was ${materialNumber}-${material.latestVersion}.`);
                }
            } else {
                this.addMaterialMessage(WarningsEnum.WARNING, `Material not yet sent from ${this.supplyingPackingPlantCode}.`);
            }
        }
    }

    toggleEnabledMaterialNumberInput = (): void => {
        if (this.selectedMaterial != null) {
            this.material.disable();
        }
    };

    setSelectedMaterialFormValues = (): void => {
        if (this.selectedMaterial != null) {
            this.material.setValue(this.selectedMaterial.material);
            this.lotNumber.setValue(this.selectedMaterial.lotNumber);
            this.mcdrNumber.setValue(this.selectedMaterial.mcdrNumber);
            this.igefNumber.setValue(this.selectedMaterial.igefNumber);
            this.colourCode.setValue(this.selectedMaterial.colourCode);
            this.modelType.setValue(this.selectedMaterial.modelType);
            this.orderedQuantity.setValue(this.selectedMaterial.orderedQuantity);
        }
    };

    managePadding = (): void => {
        const materialErrors = this.materialMessages.get(WarningsEnum.ERROR);
        const materialWarnings = this.materialMessages.get(WarningsEnum.WARNING);
        timer(0)
                .subscribe(() => {
                    this.reduceMaterialPadding = materialErrors && materialErrors.length > 0
                            && materialWarnings && materialWarnings.length > 0;
                    this.reduceOrderedQuantityPadding = this.orderedQuantityMessages.size > 0;
                });
    };

    addMaterialMessage = (level: WarningsEnum, message: string): void => {
        if (this.materialMessages.has(level)) {
            this.materialMessages.get(level)
                    .push(message);
        } else {
            this.materialMessages.set(level, [message]);
        }

        this.materialMessages = new Map(this.materialMessages);
    };

    clearMaterialMessages = (): void => {
        this.materialMessages.clear();

        this.materialMessages = new Map(this.materialMessages);
    };

    setOrderedQuantityMessage = (level: WarningsEnum, message: string): void => {
        this.orderedQuantityMessages.set(level, [message]);

        this.orderedQuantityMessages = new Map(this.orderedQuantityMessages);
    };

    clearOrderedQuantityMessages = (): void => {
        this.orderedQuantityMessages.clear();

        this.orderedQuantityMessages = new Map(this.orderedQuantityMessages);
    };

    private resetForm = (): void => {
        this.material.reset();
        this.modelType.reset();
        this.lotNumber.reset();
        this.mcdrNumber.reset();
        this.orderedQuantity.reset();
        this.igefNumber.reset();
        this.colourCode.reset();
    };

    private evaluateCurrentResultsReset(term: string): void {
        if (term !== this.state.previousSearchTerm) {
            this.storeState({
                savedMaterials: [],
                firstResultIndex: 0,
                previousSearchTerm: term
            });
        }
    }
}
